import { ColumnPinningState } from "@tanstack/react-table"
import { CHECKBOX_COLUMN_ID } from "@tfg/ui"
import {
  createContext,
  useState,
  PropsWithChildren,
  useContext,
  SetStateAction,
  Dispatch,
  useCallback,
  useEffect,
} from "react"
import { useSearchParams } from "react-router-dom"

import { Sorts } from "~/components/SortBuilder"
import { FindViewByIdResponseDto, useFindViewById } from "~/gen/api"

type ContextType<T> = {
  isLoading: boolean
  view: FindViewByIdResponseDto | null
  orderedColumns: string[]
  columnPinning: ColumnPinningState
  columnVisibility: Record<string, boolean>
  initialColumnPinning: ColumnPinningState | undefined
  initialColumnVisibility: Record<string, boolean> | undefined
  initialColumnOrder: string[] | undefined
  setOrderedColumns: Dispatch<SetStateAction<string[]>>
  setColumnPinning: Dispatch<SetStateAction<ColumnPinningState>>
  setColumnVisibility: Dispatch<SetStateAction<Record<string, boolean>>>
  sorts: Sorts
  setSorts: Dispatch<SetStateAction<Sorts>>
  filters: T
  setFilters: Dispatch<SetStateAction<T>>
  reset: () => void
}

const Context = createContext<ContextType<any>>({
  isLoading: true,
  view: null,
  orderedColumns: [],
  columnPinning: { left: [CHECKBOX_COLUMN_ID], right: [] },
  columnVisibility: {},
  initialColumnPinning: { left: [CHECKBOX_COLUMN_ID], right: [] },
  initialColumnVisibility: {},
  initialColumnOrder: [],
  setOrderedColumns: () => {},
  setColumnPinning: () => {},
  setColumnVisibility: () => {},
  sorts: {},
  setSorts: () => undefined,
  filters: {},
  setFilters: () => undefined,
  reset: () => undefined,
})

export const useHeaderControlsContext = () => {
  const context = useContext(Context)
  if (!context) {
    throw new Error(
      "useHeaderControlsContext must be used within a HeaderControlsProvider"
    )
  }
  return context
}

export const HeaderControlsProvider = <T,>({
  children,
  initialColumnPinning,
  initialColumnVisibility,
  initialColumnOrder,
}: PropsWithChildren<{
  initialColumnPinning?: ColumnPinningState
  initialColumnVisibility?: Record<string, boolean>
  initialColumnOrder?: string[]
}>) => {
  const {
    view,
    isLoading,
    orderedColumns,
    columnPinning,
    columnVisibility,
    sorts,
    filters,
    setOrderedColumns,
    setColumnPinning,
    setColumnVisibility,
    setSorts,
    setFilters,
  } = useView<T>({
    initialColumnOrder,
    initialColumnPinning,
    initialColumnVisibility,
  })

  const reset = useCallback(() => {
    setOrderedColumns(
      view?.data?.pinnedColumns?.columnOrder ?? initialColumnOrder ?? []
    )
    setColumnPinning(
      view?.data?.pinnedColumns?.columnPinning ?? {
        left: [CHECKBOX_COLUMN_ID, ...(initialColumnPinning?.left ?? [])],
        right: initialColumnPinning?.right ?? [],
      }
    )
    setColumnVisibility(
      view?.data?.pinnedColumns?.columnVisibility ??
        initialColumnVisibility ??
        {}
    )
    setSorts((view?.data?.sorts as Sorts) ?? {})
    setFilters((view?.data?.filters as T) ?? ({} as T))
  }, [
    view,
    setOrderedColumns,
    setColumnPinning,
    setColumnVisibility,
    setSorts,
    setFilters,
    initialColumnPinning,
    initialColumnOrder,
    initialColumnVisibility,
  ])

  useEffect(() => {
    reset()
  }, [view, reset])

  return (
    <Context.Provider
      value={{
        isLoading,
        view: view?.data ?? null,
        initialColumnPinning,
        initialColumnVisibility,
        initialColumnOrder,
        orderedColumns,
        columnPinning,
        columnVisibility,
        setOrderedColumns,
        setColumnPinning,
        setColumnVisibility,
        sorts,
        setSorts,
        filters,
        setFilters,
        reset,
      }}
    >
      {children}
    </Context.Provider>
  )
}

const useView = <T,>({
  initialColumnOrder,
  initialColumnPinning,
  initialColumnVisibility,
}: {
  initialColumnOrder?: string[]
  initialColumnPinning?: ColumnPinningState
  initialColumnVisibility?: Record<string, boolean>
}) => {
  const [searchParams] = useSearchParams()
  const viewId = searchParams.get("viewId") ?? ""
  const { data: view, isLoading } = useFindViewById(viewId, {
    query: {
      enabled: !!viewId,
    },
  })
  const [orderedColumns, setOrderedColumns] = useState<string[]>(
    view?.data?.pinnedColumns?.columnOrder ?? initialColumnOrder ?? []
  )
  const [columnPinning, setColumnPinning] = useState<ColumnPinningState>({
    left: [CHECKBOX_COLUMN_ID, ...(initialColumnPinning?.left ?? [])],
    right: [],
  })
  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >(
    view?.data?.pinnedColumns?.columnVisibility ?? initialColumnVisibility ?? {}
  )
  const [sorts, setSorts] = useState<Sorts>({})
  const [filters, setFilters] = useState<T>(
    (view?.data?.filters as T) ?? ({} as T)
  )
  return {
    view,
    isLoading,
    orderedColumns,
    columnPinning,
    columnVisibility,
    sorts,
    filters,
    setOrderedColumns,
    setColumnPinning,
    setColumnVisibility,
    setSorts,
    setFilters,
  }
}
